import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Clock, Sparkles, Lock, Share2 } from "lucide-react";

const CATEGORY_LABEL = {
  algorithm: "Algorithm",
  growth: "Growth",
  money: "Money",
  craft: "Craft",
};

export default function PlaybookDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    const endpoint = user ? `/playbooks/${slug}` : `/playbooks/public/${slug}`;
    api
      .get(endpoint)
      .then((r) => setP(r.data))
      .catch(() => {
        toast.error("Playbook not found");
        nav(user ? "/app/playbooks" : "/playbooks");
      });
  }, [slug, nav, user]);

  const complete = async () => {
    setCompleting(true);
    try {
      await api.post(`/playbooks/${slug}/complete`);
      setP({ ...p, read: true });
      toast.success("Marked as read. Now go do it.");
    } finally {
      setCompleting(false);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/playbooks/${slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: p.title, text: p.one_liner, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Public link copied.");
      }
    } catch {
      // user cancelled or unsupported
    }
  };

  if (!p) return <div className="p-8 text-neutral-500 font-mono">Loading…</div>;
  const isPublic = !user;
  const backTo = user ? "/app/playbooks" : "/playbooks";

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <SEO
        title={p.title}
        description={p.one_liner}
        path={`/playbooks/${slug}`}
        type="article"
      />
      <div className="flex items-center justify-between mb-8">
        <Link
          to={backTo}
          className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-white flex items-center gap-1"
          data-testid="playbook-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> library
        </Link>
        {user && (
          <Button
            variant="ghost"
            size="sm"
            onClick={share}
            className="rounded-full border border-white/15"
            data-testid="playbook-share-btn"
          >
            <Share2 className="w-3 h-3" /> Share
          </Button>
        )}
      </div>

      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#CCFF00]">
            {CATEGORY_LABEL[p.category]}
          </span>
          <span className="text-neutral-600">·</span>
          <span className="text-xs font-mono text-neutral-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {p.read_time_min} min
          </span>
          <span className="text-neutral-600">·</span>
          <span className="text-xs font-mono text-neutral-500">{p.difficulty}</span>
        </div>
        <h1
          className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-tight mb-4"
          data-testid="playbook-title"
        >
          {p.title}
        </h1>
        <p className="text-lg text-neutral-300 leading-relaxed">{p.one_liner}</p>
      </div>

      {/* Action today card — locked for anonymous visitors */}
      {isPublic ? (
        <div
          className="p-6 rounded-2xl bg-[#141414] border border-[#CCFF00]/30 mb-10 relative overflow-hidden"
          data-testid="playbook-action-gate"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#141414]/80 to-[#141414] pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-3 h-3 text-[#CCFF00]" />
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#CCFF00]">
                // action today · locked
              </p>
            </div>
            <p className="font-display font-bold text-lg text-neutral-500 blur-sm select-none" aria-hidden="true">
              {p.action_today || "The specific action to take today, hidden behind signup."}
            </p>
            <div className="mt-6 flex items-center gap-3">
              <Link to="/signup" data-testid="playbook-signup-cta">
                <Button className="rounded-full h-11 px-6 bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold">
                  Sign up to unlock (free) <Sparkles className="w-4 h-4" />
                </Button>
              </Link>
              <span className="text-xs font-mono text-neutral-500">14 more playbooks await</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-6 rounded-2xl bg-[#CCFF00] text-black mb-10">
          <p className="font-mono text-[10px] uppercase tracking-widest text-black/60 mb-2">
            // action today
          </p>
          <p className="font-display font-bold text-lg leading-snug" data-testid="playbook-action">
            {p.action_today}
          </p>
        </div>
      )}

      {/* Body */}
      <article
        className="prose prose-invert prose-neutral max-w-none
                   prose-headings:font-display prose-headings:font-black prose-headings:tracking-tight
                   prose-h3:text-2xl prose-h3:mt-10 prose-h3:mb-4
                   prose-p:text-neutral-300 prose-p:leading-relaxed
                   prose-strong:text-white prose-strong:font-bold
                   prose-em:text-neutral-400 prose-em:italic
                   prose-blockquote:border-l-[#CCFF00] prose-blockquote:bg-white/5 prose-blockquote:pl-4 prose-blockquote:py-1 prose-blockquote:rounded-r prose-blockquote:not-italic
                   prose-code:text-[#CCFF00] prose-code:bg-transparent prose-code:before:content-[''] prose-code:after:content-['']
                   prose-li:marker:text-[#CCFF00] prose-li:text-neutral-300
                   prose-a:text-[#CCFF00] prose-a:no-underline hover:prose-a:underline
                   prose-table:border-white/10 prose-th:text-white prose-td:text-neutral-300
                   prose-hr:border-white/10"
        data-testid="playbook-body"
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.body_md}</ReactMarkdown>
      </article>

      {/* Complete CTA (authed only) or signup CTA (anon) */}
      {isPublic ? (
        <div className="mt-16 pt-8 border-t border-white/10 text-center">
          <p className="font-display font-black text-3xl tracking-tighter mb-3">
            Read one. Now unlock the other 14.
          </p>
          <p className="text-neutral-400 mb-6 max-w-md mx-auto">
            The full Playbook Library, weekly Trend Radar, and your own DAP inbox. Free forever.
          </p>
          <Link to="/signup" data-testid="playbook-bottom-cta">
            <Button className="rounded-full h-12 px-8 bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold hover:scale-105 transition-transform">
              Claim your handle <Sparkles className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      ) : (
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-neutral-500 font-mono">
            Did you take the action? Mark it done to lock it in.
          </p>
          <Button
            onClick={complete}
            disabled={completing || p.read}
            className={`rounded-full h-12 px-6 font-semibold ${
              p.read
                ? "bg-white/10 text-neutral-400"
                : "bg-[#CCFF00] text-black hover:bg-[#b8e600] hover:scale-105 transition-transform"
            }`}
            data-testid="playbook-complete-btn"
          >
            {p.read ? (
              <>
                <CheckCircle2 className="w-4 h-4" /> Completed
              </>
            ) : (
              <>
                Mark completed <Sparkles className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
